/**
 * F-27 selection-context-toolbar action registry (docs/08 §2 extension
 * point): adding a toolbar action = one `registerToolbarAction` call from a
 * feature module. The toolbar (features/selection-toolbar) looks actions up
 * here and never special-cases them, so later features (link cards,
 * translate-selection, split …) plug in without touching the toolbar.
 *
 * This module also owns the cross-feature ADD-COMMENT HOOK (F-50..F-52): the
 * toolbar's "Add comment" action delegates to whatever handler the comments
 * feature binds via `bindAddComment`. Unbound, the request is a documented
 * no-op (the button renders disabled), so the toolbar ships before the
 * comments feature lands.
 */
import type { ReactNode } from "react";

import type { BoardSession } from "../../core/session.js";

/** Axis-aligned rectangle in BOARD units (the F-121 camera space). */
export interface BoardRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * A cell-range selection as the toolbar sees it. Produced by the grid
 * (GridTable) from its live layout pass, so `rect` already reflects F-26
 * auto-grown sizes; row/column ids are STABLE ids in visual order (docs/07
 * C4: actions must re-check ids at run time if they mutate structure).
 */
export interface CellsToolbarSelection {
  kind: "cells";
  sheetId: string;
  /** Row ids of the normalized selection range, in visual order. */
  rowIds: readonly string[];
  /** Column ids of the normalized selection range, in visual order. */
  colIds: readonly string[];
  /** The selection's ANCHOR cell (comment target, docs/04 §3 cell anchor). */
  anchor: { rowId: string; colId: string; rect: BoardRect };
  /** Bounding rect of the whole selection in board units. */
  rect: BoardRect;
  /**
   * Where the POINTER was when this selection was made, in board units — the
   * click on a row handle, the end of a drag. The pill sits over it
   * (selection-toolbar/placement.ts `preferredCenterX`) rather than over the
   * selection's centre, which for a whole-row selection can be a screen away
   * from the hand. Absent for a selection the keyboard made.
   */
  pointer?: { x: number; y: number };
}

/** A selected floating element as the toolbar sees it. */
export interface ElementToolbarSelection {
  kind: "element";
  elementId: string;
  /** The element's current rect in board units. */
  rect: BoardRect;
}

export type ToolbarSelection = CellsToolbarSelection | ElementToolbarSelection;

/** What an action's `run`/`popover`/`icon`/`enabled` receive. */
export interface ToolbarActionContext {
  session: BoardSession;
  selection: ToolbarSelection;
  /** Close the toolbar's open popover (palette …), if any. */
  closePopover(): void;
}

/**
 * One pill button (F-27). `run` is the immediate action; actions that open
 * UI instead (the F-17-bg palette) provide `popover` and omit `run` — the
 * toolbar toggles the popover on click. `icon` may be a function so a button
 * can preview selection state (the bg swatch shows the current fill).
 */
export interface ToolbarAction {
  id: string;
  icon: ReactNode | ((ctx: ToolbarActionContext) => ReactNode);
  /**
   * The hover label (rendered as a `data-tip` bubble, app.css) and the
   * accessible name. A function when the label depends on what is selected —
   * F-70's icon reads "Translate this column" on a target column and
   * "Translate Original Script…" on the source.
   */
  tooltip: string | ((ctx: ToolbarActionContext) => string);
  appliesTo: "cells" | "elements" | "both";
  /** Immediate action. Ignored when `popover` is present. */
  run?(ctx: ToolbarActionContext): void;
  /** Popover renderer; the button toggles it instead of calling `run`. */
  popover?(ctx: ToolbarActionContext): ReactNode;
  /** Render the button disabled when false (default: enabled). */
  enabled?(ctx: ToolbarActionContext): boolean;
  /**
   * OMIT the button entirely when false (default: shown). `appliesTo` filters
   * by selection KIND; this filters by what is actually selected — F-70's
   * translate icon exists only for a single non-source column.
   *
   * Prefer `enabled` when the action is merely unavailable right now (a greyed
   * button is discoverable, and F-28 explicitly asks for a greyed delete);
   * use `visible` when the action is meaningless for this selection and no
   * amount of clicking would enable it, so a greyed button would only invite a
   * hunt for the setting that turns it on.
   */
  visible?(ctx: ToolbarActionContext): boolean;
  /** Sort key, ascending left→right; ties keep registration order. Default 0. */
  order?: number;
}

const registry: ToolbarAction[] = [];

/**
 * Register (or re-register — same id REPLACES, keeping Vite HMR re-imports
 * idempotent) a toolbar action.
 */
export function registerToolbarAction(action: ToolbarAction): void {
  const existing = registry.findIndex((a) => a.id === action.id);
  if (existing >= 0) registry[existing] = action;
  else registry.push(action);
}

/**
 * Actions applicable to a selection KIND, sorted by (order, registration).
 * `visible` is NOT applied here — it needs a full context (the session + the
 * selection), which only the toolbar has; the toolbar filters on it at render.
 */
export function toolbarActionsFor(kind: ToolbarSelection["kind"]): ToolbarAction[] {
  const applies = kind === "cells" ? "cells" : "elements";
  return registry
    .map((action, index) => ({ action, index }))
    .filter(({ action }) => action.appliesTo === "both" || action.appliesTo === applies)
    .sort(
      (a, b) => (a.action.order ?? 0) - (b.action.order ?? 0) || a.index - b.index,
    )
    .map(({ action }) => action);
}

/* ------------------------------------------------------------------ */
/* Add-comment hook (F-50..F-52) — bound by the comments feature       */
/* ------------------------------------------------------------------ */

/**
 * What the toolbar hands the comments feature. `rect` is the target's
 * current BOARD-unit rect so the handler can drop the pin / compute the
 * anchor offset (docs/04 §3: cell anchor = rowId+colId+(dx,dy), element
 * anchor = elementId+(dx,dy)) without re-deriving layout.
 */
export type AddCommentTarget =
  | { kind: "cell"; sheetId: string; rowId: string; colId: string; rect: BoardRect }
  | { kind: "element"; elementId: string; rect: BoardRect };

export type AddCommentHandler = (session: BoardSession, target: AddCommentTarget) => void;

let addCommentHandler: AddCommentHandler | null = null;

/**
 * Bind the comments feature's "start a thread on this target" entry point.
 * Call once at feature-module scope (before the first toolbar render — the
 * toolbar's disabled state is read at render time). Returns an unbind
 * function. Binding again replaces the previous handler (HMR-safe).
 */
export function bindAddComment(handler: AddCommentHandler): () => void {
  addCommentHandler = handler;
  return () => {
    if (addCommentHandler === handler) addCommentHandler = null;
  };
}

/** True once the comments feature has bound its handler. */
export function isAddCommentBound(): boolean {
  return addCommentHandler !== null;
}

/**
 * Invoke the bound add-comment handler. A documented NO-OP (returns false)
 * while unbound, so the toolbar works before the comments feature exists.
 */
export function requestAddComment(session: BoardSession, target: AddCommentTarget): boolean {
  if (addCommentHandler === null) return false;
  addCommentHandler(session, target);
  return true;
}
